import { animate, style, transition, trigger } from '@angular/animations';
import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  effect,
  ElementRef,
  input,
  OnDestroy,
  output,
  viewChild,
} from '@angular/core';
import { DotLottieWorker } from '@lottiefiles/dotlottie-web';
import { MarkdownModule } from 'ngx-markdown';
import { environment } from '../../../../../environments/environment';
import { ArticleSummaryView } from '../../../../_generated';
import { ChipComponent } from '../../../../ui-components/molecules';

@Component({
  selector: 'dmcom-messages-listing-message',
  standalone: true,
  imports: [NgTemplateOutlet, MarkdownModule, ChipComponent],
  styles: [
    `
      .shader-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        height: 56px;
        animation: shader-sweep 2.5s ease-in-out infinite;
        pointer-events: none;
      }

      @keyframes shader-sweep {
        0% {
          transform: translateY(100%);
        }
        100% {
          transform: translateY(-100%);
        }
      }
    `,
  ],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('300ms ease-in', style({ opacity: 1 })),
      ]),
      transition(':leave', [
        style({ opacity: 1 }),
        animate('300ms ease-out', style({ opacity: 0 })),
      ]),
    ]),
  ],
  template: `
    <!--                 -->
    <!-- Loading Message -->
    <!--                 -->
    @if (isLoading()) {
      <div
        class="rounded-lg p-16 typo-p3 bg-transparent text-transparent max-w-320"
      >
        {{ messageContent() }}
      </div>
      <!--              -->
      <!-- User Message -->
      <!--              -->
    } @else if (isFromUser()) {
      <div
        class="max-w-448 px-16 py-12 rounded-2xl typo-p3 bg-chat-message-user justify-self-end wrap-anywhere"
      >
        <markdown [data]="messageContent()"></markdown>
      </div>

      <!--                 -->
      <!-- Chatbot Message -->
      <!--                 -->
    } @else if (!isFromUser()) {
      <div class="flex flex-col gap-16">
        <!-- Chatbot Message Thinking -->
        @if (isThinking()) {
          <ng-container *ngTemplateOutlet="aiThinkingTemplate" />
        } @else {
          <!-- Chatbot Message Content -->
          <div class="w-full p-8 typo-p3 justify-self-start">
            <markdown [data]="messageContent()"></markdown>
          </div>

          <!-- SOURCES -->
          @if (messageArticles().length > 0) {
            <div class="flex flex-col gap-8 px-8">
              <span class="typo-p4 text-grayscale-gray-darker"> Sources: </span>
              @for (article of messageArticles(); let i = $index; track i) {
                <dmcom-chip
                  [title]="article.article.title"
                  (click)="openArticle(article.article.nodeId)"
                />
              }
            </div>
          }
        }
      </div>
    }

    <!-- Chatbot isThinking -->
    <ng-template #aiThinkingTemplate>
      <div
        class="
          flex justify-start items-center gap-8
          relative overflow-hidden p-16"
      >
        <div class="ai-spinner w-32 h-32">
          <canvas @fadeInOut #lottieCanvas class="size-32"></canvas>
        </div>
        <span class="typo-p2 text-primary-800 relative"
          >Analyzing Request...
          <div class="shader-overlay">
            <div
              style="height: 16px;align-self: stretch;background: linear-gradient(0deg, rgba(255, 255, 255, 0.64) 0%, rgba(255, 255, 255, 0.00) 100%);"
            ></div>
            <div
              style="height: 24px;align-self: stretch;opacity: 0.64;background: var(--Shades-White, #FFF);"
            ></div>
            <div
              style="height: 16px;align-self: stretch;background: linear-gradient(180deg, rgba(255, 255, 255, 0.64) 0%, rgba(255, 255, 255, 0.00) 100%);"
            ></div>
          </div>
        </span>
      </div>
    </ng-template>
  `,
})
export class MessagesListingMessageComponent implements OnDestroy {
  public readonly messageId = input.required<number>();
  public readonly messageContent = input.required<string>();
  public readonly messageArticles = input.required<
    {
      article: ArticleSummaryView;
    }[]
  >();
  public readonly isFromUser = input.required<boolean>();
  public readonly isThinking = input<boolean>(false);
  public readonly isProcessing = input<boolean>(false);
  public readonly isLoading = input<boolean>(false);
  public readonly isMobile = input<boolean>(false);
  public readonly openArticleClicked = output<number>();

  private lottieWorker: DotLottieWorker | null = null;
  public readonly lottieCanvas =
    viewChild<ElementRef<HTMLCanvasElement>>('lottieCanvas');

  constructor() {
    effect(() => {
      const canvas = this.lottieCanvas();
      const isThinking = this.isThinking();

      if (canvas && isThinking) {
        this.initializeLottieAnimation(canvas.nativeElement);
      } else {
        this.cleanupLottieAnimation();
      }
    });
  }

  ngOnDestroy(): void {
    this.cleanupLottieAnimation();
  }

  private initializeLottieAnimation(canvas: HTMLCanvasElement): void {
    if (this.lottieWorker) {
      return; // Already initialized
    }

    try {
      this.lottieWorker = new DotLottieWorker({
        canvas,
        src: `${environment.appUiUrl}/assets/lottie/ai-thinking.lottie`,
        autoplay: true,
        loop: true,
        speed: 0.85,
      });
    } catch (error) {
      console.error('Failed to initialize Lottie animation:', error);
    }
  }

  private cleanupLottieAnimation(): void {
    if (this.lottieWorker) {
      void this.lottieWorker.destroy();
      this.lottieWorker = null;
    }
  }

  openArticle(nodeId: number) {
    this.openArticleClicked.emit(nodeId);
  }
}
